import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Input, Select, Space, Button, Modal } from "antd";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import type {
  FilterDropdownProps,
  ColumnType,
  TablePaginationConfig,
  FilterValue,
} from "antd/es/table/interface";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { addNotification } from "../store/slices/notificationSlice";
import { useNavigate } from "react-router-dom";

const loanPurposeMap: Record<string, string> = {
  PURCHASE_HOUSE: "Purchase - House",
  PURCHASE_FLAT: "Purchase - Flat / Floor",
  PURCHASE_PLOT: "Purchase - Plot Only",
  PURCHASE_PLOT_CONSTRUCTION: "Purchase - Plot + Construction",
  PURCHASE_SELLER_BT: "Purchase - Seller BT",
  PURCHASE_NON_RESIDENTIAL_PROPERTY: "Purchase - Non-Residential Property",
  CONSTRUCTION_EXTENSION_RENOVATION: "Construction/Extension/Renovation",
  BALANCE_TRANSFER_HOME_LOAN: "Balance Transfer - Home Loan",
  BALANCE_TRANSFER_HL_TOP_UP: "Balance Transfer - HL + Top Up",
  BALANCE_TRANSFER_LAP: "Balance Transfer - LAP",
  BALANCE_TRANSFER_LAP_TOP_UP: "Balance Transfer - LAP + Top Up",
  LOAN_AGAINST_RESIDENTIAL_PROPERTY: "Loan Against - Residential Property",
  LOAN_AGAINST_PLOT: "Loan Against - Plot",
  LOAN_AGAINST_NON_RESIDENTIAL_PROPERTY:
    "Loan Against - Non-Residential Property",
};

interface Notification {
  id: number;
  ownerId: number;
  isRead: boolean;
  message: string;
  entryDate: string;
  modDate: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface FileLogin {
  id: number;
  name: string;
  leadId: string;
  phoneNumber: string;
  panCard: string;
  loanAmount: number;
  loanType: string;
  loanPurpose: string;
  entryDate: string;
  modDate: string;
  status:
    | "LOGIN_QUERY"
    | "WIP"
    | "PD_QUERIES"
    | "SOFT_SANCTION"
    | "FINAL_SANCTION"
    | "REJECTED"
    | "DISBURSED"
    | "PDD_PENDING"
    | "READY_FOR_BILLING";
  dateOfBirth: string;
  residenceType: "INDIAN" | "NRI";
  customerProfile: "SALARIED" | "SELF_EMPLOYED";
  notifications?: Notification[];
  user: User;
}

interface PaginatedResponse {
  content: FileLogin[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface TableFilters {
  [key: string]: string[] | null;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const StatusIcon: React.FC<{ status: FileLogin["status"] }> = ({ status }) => {
  const getStatusIcon = () => {
    switch (status) {
      case "LOGIN_QUERY":
        return "❓";
      case "WIP":
        return "🔄";
      case "PD_QUERIES":
        return "📝";
      case "SOFT_SANCTION":
        return "📋";
      case "FINAL_SANCTION":
        return "✅";
      case "REJECTED":
        return "❌";
      case "DISBURSED":
        return "💰";
      case "PDD_PENDING":
        return "⏳";
      case "READY_FOR_BILLING":
        return "📊";
      default:
        return "❓";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "LOGIN_QUERY":
        return "#f59e0b";
      case "WIP":
        return "#3b82f6";
      case "PD_QUERIES":
        return "#8b5cf6";
      case "SOFT_SANCTION":
        return "#10b981";
      case "FINAL_SANCTION":
        return "#10b981";
      case "REJECTED":
        return "#ef4444";
      case "DISBURSED":
        return "#6366f1";
      case "PDD_PENDING":
        return "#f59e0b";
      case "READY_FOR_BILLING":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  return (
    <span
      style={{
        color: getStatusColor(),
        fontSize: "1.2em",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
    >
      {getStatusIcon()} {status}
    </span>
  );
};

const Dashboard: React.FC = () => {
  const [fileLogins, setFileLogins] = useState<FileLogin[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [name, setName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [panCard, setPanCard] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(100);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TableFilters>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [newNotificationMessage, setNewNotificationMessage] =
    useState<string>("");
  const [isSubmittingNotification, setIsSubmittingNotification] =
    useState<boolean>(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          searchTerm: debouncedSearchTerm,
          page: page.toString(),
          size: size.toString(),
          ...(name && { name }),
          ...(phoneNumber && { phoneNumber }),
          ...(panCard && { panCard }),
        });

        // Add filter parameters
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value.length > 0) {
            params.append(key, value.join(","));
          }
        });

        const { data } = await axios.get(
          `/file-login/list?${params.toString()}`
        );

        if (data) {
          setFileLogins(data.content);
          setTotalPages(data.totalPages);
          setTotalElements(data.totalElements);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchTerm, name, phoneNumber, panCard, page, size, filters]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    extra: any
  ) => {
    setPage((pagination.current || 1) - 1);
    setSize(pagination.pageSize || 10);

    // Convert filters to the format we need
    const formattedFilters: TableFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        formattedFilters[key] = value as string[];
      }
    });
    setFilters(formattedFilters);
  };

  const handleExpand = (expanded: boolean, record: FileLogin) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
      // Fetch notifications for this file login
      fetchNotifications(record.id);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
    }
  };

  const fetchNotifications = async (fileLoginId: number) => {
    try {
      const { data } = await axios.get(`/notifications/owner/${fileLoginId}`);
      setFileLogins((prev) =>
        prev.map((fl) =>
          fl.id === fileLoginId ? { ...fl, notifications: data } : fl
        )
      );
    } catch (error) {
      addNotification({
        type: "error",
        message: "Failed to fetch notification",
      });
    }
  };

  const handleCreateNotification = async (fileLoginId: number) => {
    if (!newNotificationMessage.trim()) return;

    setIsSubmittingNotification(true);
    try {
      await axios.post(`/notifications`, {
        message: newNotificationMessage,
        ownerId: fileLoginId,
      });
      setNewNotificationMessage("");
      fetchNotifications(fileLoginId);
      dispatch(
        addNotification({
          type: "success",
          message: "Notification created successfully",
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to create notification",
        })
      );
    } finally {
      setIsSubmittingNotification(false);
    }
  };

  const handleDeleteNotification = async (
    fileLoginId: number,
    notificationId: number
  ) => {
    try {
      await axios.delete(`/notifications/${notificationId}`);
      fetchNotifications(fileLoginId);
    } catch (error) {
      addNotification({
        type: "error",
        message: "Failed to delete notification",
      });
    }
  };

  const columns: ColumnType<FileLogin>[] = [
    {
      title: "ID",
      dataIndex: "leadId",
      key: "id",
      width: 70,
      render: (id: number) => (
        <Button type="link" onClick={() => navigate(`/file-login/${id}`)}>
          {id}
        </Button>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      ellipsis: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys?.[0] as string}
            onChange={(e) =>
              setSelectedKeys?.(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm?.()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm?.()}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value: React.Key | boolean, record: FileLogin) =>
        record.name.toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: "Loan Amount",
      dataIndex: "loanAmount",
      key: "loanAmount",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Loan Type",
      dataIndex: "loanType",
      key: "loanType",
      width: 120,
      ellipsis: true,
      filters: [
        { text: "Home Loan", value: "HOME" },
        { text: "Non Home Loan", value: "NON_HOME" },
      ],
      onFilter: (value: React.Key | boolean, record: FileLogin) =>
        record.loanType === value,
    },
    {
      title: "Loan Purpose",
      dataIndex: "loanPurpose",
      key: "loanPurpose",
      width: 180,
      ellipsis: true,
      render: (purpose: string) => loanPurposeMap[purpose] || purpose,
    },
    {
      title: "Generated By",
      dataIndex: "user",
      key: "loanPurpose",
      width: 180,
      ellipsis: true,
      render: (user: User) => user["firstName"] + " " + user["lastName"],
    },
    {
      title: "Entry Date",
      dataIndex: "entryDate",
      key: "entryDate",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      filters: [
        { text: "Login Query", value: "LOGIN_QUERY" },
        { text: "WIP", value: "WIP" },
        { text: "PD Queries", value: "PD_QUERIES" },
        { text: "Soft Sanction", value: "SOFT_SANCTION" },
        { text: "Final Sanction", value: "FINAL_SANCTION" },
        { text: "Rejected", value: "REJECTED" },
        { text: "Disbursed", value: "DISBURSED" },
        { text: "PDD Pending", value: "PDD_PENDING" },
        { text: "Ready for Billing", value: "READY_FOR_BILLING" },
      ],
      onFilter: (value: React.Key | boolean, record: FileLogin) =>
        record.status === value,
      render: (status: FileLogin["status"]) => <StatusIcon status={status} />,
    },
  ];

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <Input
            placeholder="Search by name, phone, or PAN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
        </div>
      </header>

      <main className="dashboard-content">
        {isLoading ? (
          <div className="loading">Loading data...</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <Table
            columns={columns}
            dataSource={fileLogins}
            rowKey="id"
            pagination={{
              current: page + 1,
              pageSize: size,
              total: totalElements,
              showSizeChanger: true,
              showQuickJumper: true,
              responsive: true,
              showTotal: (total) => `Total ${total} items`,
              size: "small",
              pageSizeOptions: ["100", "200", "300"],
            }}
            loading={isLoading}
            size="small"
            scroll={{ x: true, y: "calc(100vh - 75px - 12rem)" }}
            style={{ width: "100%" }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => navigate(`/file-login/${record.id}`),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </main>
    </div>
  );
};

export default Dashboard;
